import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Menu, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const Navbar = ({ onLoginClick }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleLogin = () => {
    if (onLoginClick) {
      onLoginClick();
    }
  };

  const handleHomeClick = () => {
    navigate('/app');
  };

  const handleBrandClick = (e) => {
    e.preventDefault();
    // 如果当前在首页，则刷新页面
    if (window.location.pathname === '/') {
      window.location.reload();
    } else {
      // 如果不在首页，则导航到首页
      navigate('/');
    }
  };

  return (
    <NavContainer>
      <NavContent>
        <LeftGroup>
          <Brand onClick={handleBrandClick}>
            <BrandIcon src="/images/logos/logo.svg" alt="职唛 AI Logo" />
            <BrandText>职唛 AI</BrandText>
          </Brand>
        </LeftGroup>

        <DesktopNav>
          <NavText>简历诊断</NavText>
          <NavText>面试押题</NavText>
          <NavText>模拟面试</NavText>
          <NavText>会议助手</NavText>
        </DesktopNav>

        <UserActions>
          {isAuthenticated ? (
            <LoginButton onClick={handleHomeClick}>主页</LoginButton>
          ) : (
            <LoginButton onClick={handleLogin}>登录/注册</LoginButton>
          )}
        </UserActions>

        <MobileMenuButton onClick={toggleMenu}>
          {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </MobileMenuButton>
      </NavContent>

      <MobileNav isOpen={isMenuOpen}>
        <MobileNavText>简历诊断</MobileNavText>
        <MobileNavText>面试押题</MobileNavText>
        <MobileNavText>模拟面试</MobileNavText>
        <MobileNavText>会议助手</MobileNavText>
        <MobileUserActions>
          {isAuthenticated ? (
            <LoginButton onClick={handleHomeClick}>主页</LoginButton>
          ) : (
            <LoginButton onClick={handleLogin}>登录/注册</LoginButton>
          )}
        </MobileUserActions>
      </MobileNav>
    </NavContainer>
  );
};

const NavContainer = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  z-index: 1000;
`;

const NavContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 1rem 2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media (min-width: 1920px) {
    max-width: 1500px;
    padding: 1.25rem 3rem;
  }

  @media (min-width: 2560px) {
    max-width: 1700px;
    padding: 1.5rem 4rem;
  }
`;

const LeftGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  @media (min-width: 1920px) {
    gap: 12px;
  }

  @media (min-width: 2560px) {
    gap: 14px;
  }
`;

const Brand = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 700;
  font-size: clamp(1.25rem, 1.5vw, 1.75rem);
  color: #1a1a1a;
  text-decoration: none;
  cursor: pointer;
  transition: opacity 0.2s ease;

  @media (min-width: 1920px) {
    font-size: clamp(1.5rem, 1.8vw, 2rem);
    gap: 0.75rem;
  }

  @media (min-width: 2560px) {
    font-size: clamp(1.75rem, 2vw, 2.25rem);
    gap: 1rem;
  }
  
  &:hover {
    opacity: 0.8;
  }
`;

const BrandIcon = styled.img`
  width: 32px;
  height: 32px;
  object-fit: contain;
  border-radius: 8px;
  transition: all 0.2s ease;

  @media (min-width: 1920px) {
    width: 40px;
    height: 40px;
  }

  @media (min-width: 2560px) {
    width: 48px;
    height: 48px;
  }
`;

const BrandText = styled.span`
  color: #2c3e50;
`;


const DesktopNav = styled.div`
  display: flex;
  gap: 2rem;

  @media (min-width: 1920px) {
    gap: 2.5rem;
  }

  @media (min-width: 2560px) {
    gap: 3rem;
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;



const NavText = styled.span`
  display: flex;
  align-items: center;
  color: #374151;
  padding: 0.5rem 1rem;
  font-size: clamp(0.9rem, 1vw, 1.1rem);
  user-select: none;

  @media (min-width: 1920px) {
    font-size: clamp(1rem, 1.2vw, 1.2rem);
    padding: 0.625rem 1.25rem;
  }

  @media (min-width: 2560px) {
    font-size: clamp(1.1rem, 1.3vw, 1.3rem);
    padding: 0.75rem 1.5rem;
  }
`;

const UserActions = styled.div`
  display: flex;
  gap: 1rem;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const LoginButton = styled.button`
  background: linear-gradient(45deg, #6366F1, #8B5CF6);
  border: none;
  color: white;
  padding: clamp(0.5rem, 0.8vw, 0.75rem) clamp(1rem, 1.4vw, 1.5rem);
  border-radius: 25px;
  cursor: pointer;
  transition: transform 0.2s;
  font-size: clamp(0.9rem, 1vw, 1.1rem);
  font-weight: 600;

  @media (min-width: 1920px) {
    padding: 0.75rem 1.6rem;
    font-size: clamp(1rem, 1.2vw, 1.2rem);
    border-radius: 30px;
  }

  @media (min-width: 2560px) {
    padding: 0.875rem 1.9rem;
    font-size: clamp(1.1rem, 1.3vw, 1.3rem);
    border-radius: 35px;
  }

  &:hover {
    transform: translateY(-1px);
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: #374151;
  cursor: pointer;
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const MobileNav = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: ${props => props.isOpen ? 'flex' : 'none'};
    flex-direction: column;
    background: rgba(255, 255, 255, 0.98);
    backdrop-filter: blur(20px);
    padding: 1rem 2rem;
    border-top: 1px solid rgba(0, 0, 0, 0.1);
  }
`;



const MobileNavText = styled.div`
  display: flex;
  align-items: center;
  color: #374151;
  padding: 1rem 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  font-size: 1rem;
  user-select: none;
`;

const MobileUserActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 1rem;
`;

export default Navbar;
